import { callable } from './callable'

// ===========================================================================
// Types mirroring functions/src/edu/enquiries.ts (dates as ISO strings)
// ===========================================================================

export type EduEnquiryStage =
  | 'NEW'
  | 'CONTACTED'
  | 'TRIAL_BOOKED'
  | 'TRIAL_DONE'
  | 'ADMITTED'
  | 'LOST'

export type EduEnquirySource =
  | 'FORM'
  | 'WALK_IN'
  | 'PHONE'
  | 'REFERRAL'
  | 'OTHER'

export type EduEnquiryLostReason =
  | 'PRICE'
  | 'TIMING'
  | 'DISTANCE'
  | 'CHOSE_OTHER'
  | 'NO_RESPONSE'
  | 'OTHER'

export interface EduEnquiryContact {
  name: string
  phone?: string
  email?: string
  relation?: string
}

export interface EduEnquiryLearner {
  name: string
  dob?: string
  isMinor: boolean
}

export interface EduEnquiryConsentAccepted {
  at: string
  noticeVersion: string
}

export interface EduEnquiryAdmissionRecord {
  learnerId: string
  joinCodeId: string
  requestId?: string | null
  at?: string | null
}

export interface EduEnquiry {
  id: string
  workspaceId: string
  stage: EduEnquiryStage
  source: EduEnquirySource
  contact: EduEnquiryContact
  learner: EduEnquiryLearner
  interestNodeId?: string | null
  interestUseCase: 'TEACHING' | 'TRAINING'
  ownerUid?: string | null
  followUpAt?: string | null
  lostReason?: EduEnquiryLostReason | null
  lostNote?: string | null
  extraAnswers?: Record<string, unknown> | null
  learnerId?: string | null
  createdAt: string
  updatedAt: string
  closedAt?: string | null
  phoneNorm?: string
  emailNorm?: string
  consentAccepted?: EduEnquiryConsentAccepted
  requestId?: string
  admissionRequestId?: string | null
  admission?: EduEnquiryAdmissionRecord | null
}

export type EduEnquiryEventType =
  | 'CREATED'
  | 'STAGE'
  | 'ASSIGN'
  | 'FOLLOW_UP'
  | 'NOTE'
  | 'EDIT'
  | 'TRIAL'
  | 'TRIAL_ATTENDANCE'

export interface EduEnquiryEvent {
  id: string
  type: EduEnquiryEventType
  actor: string
  at: string
  before?: Record<string, unknown> | null
  after?: Record<string, unknown> | null
  text?: string
}

export interface EduEnquiryLinkPrivacy {
  noticeVersion: string
  retentionMonths: number
}

export interface EduEnquiryLink {
  id: string
  workspaceId: string
  active: boolean
  formId?: string | null
  interestNodeIds: string[]
  privacy: EduEnquiryLinkPrivacy
  createdBy: string
  createdAt: string
  rotatedAt?: string | null
  updatedAt?: string | null
}

// ===========================================================================
// Callable inputs & results
// ===========================================================================

export interface CreateEduEnquiryInput {
  workspaceId: string
  requestId: string
  source: Exclude<EduEnquirySource, 'FORM'>
  contact: EduEnquiryContact
  learner: EduEnquiryLearner
  interestNodeId?: string | null
  interestUseCase: 'TEACHING' | 'TRAINING'
  ownerUid?: string | null
  followUpAt?: string | null
}

export interface CreateEduEnquiryResult {
  enquiryId: string
}

export interface UpdateEduEnquiryInput {
  workspaceId: string
  enquiryId: string
  contact?: Partial<EduEnquiryContact>
  learner?: Partial<EduEnquiryLearner>
  interestNodeId?: string | null
  interestUseCase?: 'TEACHING' | 'TRAINING'
}

export interface UpdateEduEnquiryResult {
  success: boolean
}

export interface SetEduEnquiryStageInput {
  workspaceId: string
  enquiryId: string
  stage: EduEnquiryStage
  lostReason?: EduEnquiryLostReason
  lostNote?: string
}

export interface SetEduEnquiryStageResult {
  success: boolean
}

export interface AssignEduEnquiryInput {
  workspaceId: string
  enquiryId: string
  ownerUid: string | null
}

export interface AssignEduEnquiryResult {
  success: boolean
}

export interface SetEduEnquiryFollowUpInput {
  workspaceId: string
  enquiryId: string
  followUpAt: string | null
}

export interface SetEduEnquiryFollowUpResult {
  success: boolean
}

export interface AddEduEnquiryNoteInput {
  workspaceId: string
  enquiryId: string
  text: string
}

export interface AddEduEnquiryNoteResult {
  success: boolean
  eventId: string
}

export interface ListEduEnquiriesInput {
  workspaceId: string
  stage?: EduEnquiryStage | EduEnquiryStage[]
  interestNodeId?: string | null
  ownerUid?: string
  source?: EduEnquirySource
  followUpDue?: boolean
  text?: string
  cursor?: string
  limit?: number
}

export interface ListEduEnquiriesResult {
  enquiries: EduEnquiry[]
  nextCursor: string | null
}

export interface GetEduEnquiryInput {
  workspaceId: string
  enquiryId: string
  cursor?: string
  limit?: number
  eventsCursor?: string
  eventsLimit?: number
}

export interface GetEduEnquiryResult {
  enquiry: EduEnquiry
  events: EduEnquiryEvent[]
  nextEventsCursor: string | null
}

export interface FindEduEnquiryDuplicatesInput {
  workspaceId: string
  phone?: string
  email?: string
}

export interface FindEduEnquiryDuplicatesResult {
  duplicateEnquiries: EduEnquiry[]
  duplicateLearners: Array<{ learnerId: string; displayName: string }>
}

export interface CreateEduEnquiryLinkInput {
  workspaceId: string
  formId?: string | null
  interestNodeIds?: string[]
  privacy?: {
    noticeVersion?: string
    retentionMonths?: number
  }
}

export interface CreateEduEnquiryLinkResult {
  linkId: string
  token: string
}

export interface RotateEduEnquiryLinkInput {
  workspaceId: string
  linkId?: string
}

export interface RotateEduEnquiryLinkResult {
  token: string
}

export interface SetEduEnquiryLinkActiveInput {
  workspaceId: string
  linkId?: string
  active: boolean
}

export interface SetEduEnquiryLinkActiveResult {
  success: boolean
}

export interface UpdateEduEnquiryLinkInput {
  workspaceId: string
  linkId?: string
  formId?: string | null
  interestNodeIds?: string[]
  retentionMonths?: number
  noticeVersion?: string
}

export interface UpdateEduEnquiryLinkResult {
  success: boolean
}

export interface GetEduEnquiryLinkInput {
  workspaceId: string
  linkId?: string
}

export interface GetEduEnquiryLinkResult {
  link: Omit<EduEnquiryLink, 'tokenHash'> | null
}

export interface ReadEduEnquiryFormInput {
  token: string
}

export interface ReadEduEnquiryFormResult {
  brand: { name: string; logoUrl: string | null; accentColor: string }
  interests: Array<{ id: string; name: string; useCase: 'TEACHING' | 'TRAINING' }>
  extraFields: unknown[] | null
  privacy: EduEnquiryLinkPrivacy
}

export interface SubmitEduEnquiryInput {
  token: string
  contact: EduEnquiryContact
  learner: EduEnquiryLearner
  interestNodeId?: string | null
  extraAnswers?: Record<string, unknown>
  consent: {
    accepted: boolean
    noticeVersion: string
  }
  website?: string
}

export interface SubmitEduEnquiryResult {
  success: boolean
  enquiryId: string
}

export interface CreateEduTrialInput {
  workspaceId: string
  enquiryId: string
  requestId?: string
}

export interface CreateEduTrialResult {
  success: boolean
  trialLearnerId: string
  learnerId: string
  created: boolean
}

export interface BookEduTrialInput {
  workspaceId: string
  enquiryId: string
  seriesId: string
  date: string
  allowRepeat?: boolean
  requestId?: string
}

export interface BookEduTrialResult {
  success: boolean
  bookingId?: string
  visitId?: string
  trialType: 'BOOKING' | 'VISIT'
}

export interface CancelEduTrialInput {
  workspaceId: string
  enquiryId: string
  visitId?: string
  bookingId?: string
  requestId?: string
}

export interface CancelEduTrialResult {
  success: boolean
}

export interface AdmitEduEnquiryInput {
  workspaceId: string
  enquiryId: string
  nodeId: string
  isMinor: boolean
  guardianEmail?: string
  guardianPhone?: string
  institutionAttests?: boolean
  feePlanId?: string
  requestId?: string
}

export interface AdmitEduEnquiryResult {
  success: boolean
  learnerId: string
  joinCodeId: string
  code?: string
  pin?: string
  expiresAtMs?: number
  codeAlreadyIssued?: boolean
}

export interface GetEduAdmissionsSettingsInput {
  workspaceId: string
}

export interface GetEduAdmissionsSettingsResult {
  ackEmail: boolean
  trialEmail: boolean
}

export interface SetEduAdmissionsSettingsInput {
  workspaceId: string
  ackEmail?: boolean
  trialEmail?: boolean
}

export interface SetEduAdmissionsSettingsResult {
  success: boolean
}

// ===========================================================================
// Service functions wrapping callables
// ===========================================================================

export const createEduEnquiry = (input: CreateEduEnquiryInput): Promise<CreateEduEnquiryResult> =>
  callable<CreateEduEnquiryInput, CreateEduEnquiryResult>('createEduEnquiry', input)

export const updateEduEnquiry = (input: UpdateEduEnquiryInput): Promise<UpdateEduEnquiryResult> =>
  callable<UpdateEduEnquiryInput, UpdateEduEnquiryResult>('updateEduEnquiry', input)

export const setEduEnquiryStage = (input: SetEduEnquiryStageInput): Promise<SetEduEnquiryStageResult> =>
  callable<SetEduEnquiryStageInput, SetEduEnquiryStageResult>('setEduEnquiryStage', input)

export const assignEduEnquiry = (input: AssignEduEnquiryInput): Promise<AssignEduEnquiryResult> =>
  callable<AssignEduEnquiryInput, AssignEduEnquiryResult>('assignEduEnquiry', input)

export const setEduEnquiryFollowUp = (input: SetEduEnquiryFollowUpInput): Promise<SetEduEnquiryFollowUpResult> =>
  callable<SetEduEnquiryFollowUpInput, SetEduEnquiryFollowUpResult>('setEduEnquiryFollowUp', input)

export const addEduEnquiryNote = (input: AddEduEnquiryNoteInput): Promise<AddEduEnquiryNoteResult> =>
  callable<AddEduEnquiryNoteInput, AddEduEnquiryNoteResult>('addEduEnquiryNote', input)

export const listEduEnquiries = (input: ListEduEnquiriesInput): Promise<ListEduEnquiriesResult> =>
  callable<ListEduEnquiriesInput, ListEduEnquiriesResult>('listEduEnquiries', input)

export const getEduEnquiry = (input: GetEduEnquiryInput): Promise<GetEduEnquiryResult> =>
  callable<GetEduEnquiryInput, GetEduEnquiryResult>('getEduEnquiry', input)

export const findEduEnquiryDuplicates = (input: FindEduEnquiryDuplicatesInput): Promise<FindEduEnquiryDuplicatesResult> =>
  callable<FindEduEnquiryDuplicatesInput, FindEduEnquiryDuplicatesResult>('findEduEnquiryDuplicates', input)

export const createEduEnquiryLink = (input: CreateEduEnquiryLinkInput): Promise<CreateEduEnquiryLinkResult> =>
  callable<CreateEduEnquiryLinkInput, CreateEduEnquiryLinkResult>('createEduEnquiryLink', input)

export const rotateEduEnquiryLink = (input: RotateEduEnquiryLinkInput): Promise<RotateEduEnquiryLinkResult> =>
  callable<RotateEduEnquiryLinkInput, RotateEduEnquiryLinkResult>('rotateEduEnquiryLink', input)

export const setEduEnquiryLinkActive = (input: SetEduEnquiryLinkActiveInput): Promise<SetEduEnquiryLinkActiveResult> =>
  callable<SetEduEnquiryLinkActiveInput, SetEduEnquiryLinkActiveResult>('setEduEnquiryLinkActive', input)

export const updateEduEnquiryLink = (input: UpdateEduEnquiryLinkInput): Promise<UpdateEduEnquiryLinkResult> =>
  callable<UpdateEduEnquiryLinkInput, UpdateEduEnquiryLinkResult>('updateEduEnquiryLink', input)

export const getEduEnquiryLink = (input: GetEduEnquiryLinkInput): Promise<GetEduEnquiryLinkResult> =>
  callable<GetEduEnquiryLinkInput, GetEduEnquiryLinkResult>('getEduEnquiryLink', input)

export const readEduEnquiryForm = (input: ReadEduEnquiryFormInput): Promise<ReadEduEnquiryFormResult> =>
  callable<ReadEduEnquiryFormInput, ReadEduEnquiryFormResult>('readEduEnquiryForm', input)

export const submitEduEnquiry = (input: SubmitEduEnquiryInput): Promise<SubmitEduEnquiryResult> =>
  callable<SubmitEduEnquiryInput, SubmitEduEnquiryResult>('submitEduEnquiry', input)

export const createEduTrial = (input: CreateEduTrialInput): Promise<CreateEduTrialResult> =>
  callable<CreateEduTrialInput, CreateEduTrialResult>('createEduTrial', input)

export const bookEduTrial = (input: BookEduTrialInput): Promise<BookEduTrialResult> =>
  callable<BookEduTrialInput, BookEduTrialResult>('bookEduTrial', input)

export const cancelEduTrial = (input: CancelEduTrialInput): Promise<CancelEduTrialResult> =>
  callable<CancelEduTrialInput, CancelEduTrialResult>('cancelEduTrial', input)

export const admitEduEnquiry = (input: AdmitEduEnquiryInput): Promise<AdmitEduEnquiryResult> =>
  callable<AdmitEduEnquiryInput, AdmitEduEnquiryResult>('admitEduEnquiry', input)

export const getEduAdmissionsSettings = (input: GetEduAdmissionsSettingsInput): Promise<GetEduAdmissionsSettingsResult> =>
  callable<GetEduAdmissionsSettingsInput, GetEduAdmissionsSettingsResult>('getEduAdmissionsSettings', input)

export const setEduAdmissionsSettings = (input: SetEduAdmissionsSettingsInput): Promise<SetEduAdmissionsSettingsResult> =>
  callable<SetEduAdmissionsSettingsInput, SetEduAdmissionsSettingsResult>('setEduAdmissionsSettings', input)
